export const APPROVED_PUBLISH_ERROR_CODES: Readonly<Record<string, string>> = Object.freeze({
  quota_exceeded: 'publishing.errors.quotaExceeded',
  reconnect_required: 'publishing.errors.reconnectRequired',
  // `connection_reconnect_required` is the synchronous code the
  // `sendClipToTikTokDrafts` mutation throws at send time when the
  // connection is missing the required scope; `reconnect_required` is the
  // async `fail_reason` the status poller sets later for the same underlying
  // problem. Same user-facing message either way.
  connection_reconnect_required: 'publishing.errors.reconnectRequired',
  connection_not_found: 'publishing.errors.connectionNotFound',
  clip_not_found: 'publishing.errors.clipNotFound',
  clip_not_rendered: 'publishing.errors.clipNotRendered',
  publishing_disabled: 'publishing.errors.publishingDisabled',
  scheduled_in_past: 'publishing.errors.scheduledInPast',
  not_scheduled: 'publishing.errors.notScheduled',
  temporarily_unavailable: 'publishing.errors.temporarilyUnavailable',
  video_pull_failed: 'publishing.errors.videoPullFailed',
  file_format_check_failed: 'publishing.errors.fileFormatCheckFailed',
  rate_limit_exceeded: 'publishing.errors.rateLimitExceeded',
  invalid_title: 'publishing.errors.invalidTitle',
  description_too_long: 'publishing.errors.descriptionTooLong',
  invalid_privacy_status: 'publishing.errors.invalidPrivacyStatus',
  invalid_audience: 'publishing.errors.invalidAudience',
  media_origin_not_allowed: 'publishing.errors.mediaOriginNotAllowed',
  youtube_quota_exceeded: 'publishing.errors.youtubeQuotaExceeded',
  youtube_permission_denied: 'publishing.errors.youtubePermissionDenied',
  youtube_video_rejected: 'publishing.errors.youtubeVideoRejected',
  instagram_quota_exceeded: 'publishing.errors.instagramQuotaExceeded',
  instagram_media_rejected: 'publishing.errors.instagramMediaRejected',
  facebook_quota_exceeded: 'publishing.errors.facebookQuotaExceeded',
  facebook_media_fetch_failed: 'publishing.errors.facebookMediaFetchFailed',
  facebook_media_rejected: 'publishing.errors.facebookMediaRejected',
  facebook_publish_failed: 'publishing.errors.facebookPublishFailed',
  facebook_status_unknown: 'publishing.errors.facebookStatusUnknown',
  caption_too_long: 'publishing.errors.captionTooLong',
  timed_out: 'publishing.errors.timedOut',
  provider_unavailable: 'publishing.errors.providerUnavailable',
  // Carousel (image list) publishing.
  pro_required: 'publishing.errors.proRequired',
  too_few_images: 'publishing.errors.tooFewImages',
  too_many_images: 'publishing.errors.tooManyImages',
  carousel_unsupported: 'publishing.errors.carouselUnsupported',
  // TikTok refuses app watermarks; the backend blocks free-plan renders.
  clip_watermarked: 'publishing.errors.clipWatermarked',
});

const MAX_GRAPHQL_ERRORS_TO_INSPECT = 100;

interface OwnDataProperty {
  found: boolean;
  value?: unknown;
}

function isNonArrayObject(value: unknown): value is object {
  if (typeof value !== 'object' || value === null) return false;

  try {
    return !Array.isArray(value);
  } catch {
    return false;
  }
}

function isArray(value: unknown): value is unknown[] {
  try {
    return Array.isArray(value);
  } catch {
    return false;
  }
}

/** Reads only own data properties. Accessors, inherited properties, and
 * throwing proxy traps are treated as absent. */
function getOwnDataProperty(value: object, property: PropertyKey): OwnDataProperty {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, property);
    if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) {
      return { found: false };
    }
    return { found: true, value: descriptor.value };
  } catch {
    return { found: false };
  }
}

export function isApprovedPublishErrorCode(value: unknown): value is string {
  return typeof value === 'string'
    && Object.prototype.hasOwnProperty.call(APPROVED_PUBLISH_ERROR_CODES, value);
}

/** Extracts only explicitly approved codes from known Apollo 3 error shapes.
 * Arbitrary provider/backend messages are never parsed or returned. */
export function extractGraphqlErrorCode(
  error: unknown,
  isApprovedCode: (value: unknown) => value is string,
): string | undefined {
  if (!isNonArrayObject(error)) return undefined;

  const topLevelMessage = getOwnDataProperty(error, 'message');
  if (topLevelMessage.found && isApprovedCode(topLevelMessage.value)) {
    return topLevelMessage.value;
  }

  const graphQLErrorsProperty = getOwnDataProperty(error, 'graphQLErrors');
  if (!graphQLErrorsProperty.found || !isArray(graphQLErrorsProperty.value)) {
    return undefined;
  }

  const graphQLErrors = graphQLErrorsProperty.value;
  const lengthProperty = getOwnDataProperty(graphQLErrors, 'length');
  if (
    !lengthProperty.found
    || typeof lengthProperty.value !== 'number'
    || !Number.isSafeInteger(lengthProperty.value)
    || lengthProperty.value < 0
  ) return undefined;

  const count = Math.min(lengthProperty.value, MAX_GRAPHQL_ERRORS_TO_INSPECT);
  for (let index = 0; index < count; index += 1) {
    const graphQLErrorProperty = getOwnDataProperty(graphQLErrors, String(index));
    if (!graphQLErrorProperty.found || !isNonArrayObject(graphQLErrorProperty.value)) continue;

    const extensionsProperty = getOwnDataProperty(graphQLErrorProperty.value, 'extensions');
    if (!extensionsProperty.found || !isNonArrayObject(extensionsProperty.value)) continue;

    const extensionErrorProperty = getOwnDataProperty(extensionsProperty.value, 'error');
    if (!extensionErrorProperty.found || !isNonArrayObject(extensionErrorProperty.value)) continue;

    const messageProperty = getOwnDataProperty(extensionErrorProperty.value, 'message');
    if (messageProperty.found && isApprovedCode(messageProperty.value)) {
      return messageProperty.value;
    }
  }

  return undefined;
}

export function extractPublishErrorCode(error: unknown): string | undefined {
  return extractGraphqlErrorCode(error, isApprovedPublishErrorCode);
}

/** Maps an approved publishing failure code to an i18n key so a raw
 * provider/backend string never reaches the user. Unknown codes fall back to
 * a generic message. */
export function mapPublishError(failReason: string | null | undefined): string {
  if (!isApprovedPublishErrorCode(failReason)) return 'publishing.errors.unknown';
  return APPROVED_PUBLISH_ERROR_CODES[failReason];
}
